'use client';

import type { AuthResponse, IdentityProvidersNames } from '@repo/api-gw-sdk';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { safeReturnUrl } from '@/contexts/environment';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useNonProjectDal } from '@/contexts/useNonProjectDal';
import { getApiErrorMessage } from '@/utils/apiError';
import { type Challenge, MFA_SETUP, mfaCodeKey, NEW_PASSWORD_REQUIRED, SUPPORTED_CHALLENGES } from './challenges';
import { CredentialsStep } from './steps/credentialsStep';
import { MfaSetupStep } from './steps/mfaSetupStep';
import { MfaStep } from './steps/mfaStep';
import { NewPasswordStep } from './steps/newPasswordStep';
export const LoginV2Page = ({
  idpData
}: {
  idpData: IdentityProvidersNames;
}) => {
  const {
    nonProjectDal: dal
  } = useNonProjectDal();
  const {
    isDev
  } = useEnvironment();
  const searchParams = useSearchParams();
  const returnUrl = safeReturnUrl(searchParams.get('returnUrl'));
  const domain = searchParams.get('domain') ?? undefined;
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const ssoProviders = idpData.providers ?? [];
  const handleAuthResponse = (res: AuthResponse, email: string) => {
    if (res.ssoRedirect) {
      window.location.replace(res.ssoRedirect);
      return;
    }
    if (res.challengeName) {
      if (!SUPPORTED_CHALLENGES.has(res.challengeName)) {
        setError('This account requires a sign-in method that isn’t supported here.');
        return;
      }
      if (!res.session) {
        setError('Login could not continue. Please try again.');
        return;
      }
      setChallenge({
        session: res.session,
        email,
        challengeName: res.challengeName,
        challengeParameters: res.challengeParameters
      });
      return;
    }
    window.location.replace(returnUrl);
  };
  const runAuth = async (email_0: string, call: () => Promise<AuthResponse>, fallbackError: string) => {
    setError(null);
    setSubmitting(true);
    try {
      handleAuthResponse(await call(), email_0);
    } catch (e) {
      setError(getApiErrorMessage(e, fallbackError));
    } finally {
      setSubmitting(false);
    }
  };
  useEffect(() => {
    if (!isDev) {
      return;
    }
    dal.authV2.login({
      email: '',
      password: ''
    }, domain).then(res_0 => {
      if (!res_0.ssoRedirect && !res_0.challengeName) {
        window.location.replace(returnUrl);
      }
    }).catch(() => {
      // Leave the login form usable if the dev auto-login fails.
    });
  }, [isDev, domain, dal, returnUrl]);
  if (challenge?.challengeName === NEW_PASSWORD_REQUIRED) {
    return <NewPasswordStep error={error} submitting={submitting} onSubmit={newPassword => void runAuth(challenge.email, () => dal.authV2.respondChallenge({
      email: challenge.email,
      challengeName: NEW_PASSWORD_REQUIRED,
      session: challenge.session,
      challengeResponses: {
        NEW_PASSWORD: newPassword
      }
    }, domain), 'Could not set password')} />;
  }
  if (challenge?.challengeName === MFA_SETUP) {
    return <MfaSetupStep completing={submitting} completeError={error} onAssociate={() => dal.authV2.associateSoftwareToken({
      email: challenge.email,
      session: challenge.session
    })} onVerify={(session, userCode) => dal.authV2.verifySoftwareToken({
      session,
      userCode
    })} onComplete={session_0 => void runAuth(challenge.email, () => dal.authV2.respondChallenge({
      email: challenge.email,
      challengeName: MFA_SETUP,
      session: session_0,
      challengeResponses: {}
    }, domain), 'Could not complete MFA setup')} />;
  }
  if (challenge) {
    return <MfaStep challengeName={challenge.challengeName} challengeParameters={challenge.challengeParameters} error={error} submitting={submitting} onSubmit={code => void runAuth(challenge.email, () => dal.authV2.respondChallenge({
      email: challenge.email,
      challengeName: challenge.challengeName,
      session: challenge.session,
      challengeResponses: {
        [mfaCodeKey(challenge.challengeName)]: code
      }
    }, domain), 'Could not verify code')} />;
  }
  return <CredentialsStep error={error} submitting={submitting} ssoProviders={ssoProviders} ssoOnly={Boolean(idpData.supportsSsoOnly)} onSubmit={(email_1, password) => void runAuth(email_1, () => dal.authV2.login({
    email: email_1,
    password
  }, domain), 'Login failed')} />;
};